import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Plus, Trash2 } from 'lucide-react';
import { useCreateBucket, useUpdateBucket } from '../../hooks/useBuckets.js';
import { useCompanies } from '../../hooks/useCompanies.js';
import { useFieldDefinitions } from '../../hooks/useFieldDefinitions.js';
import { useValidationRules } from '../../hooks/useValidationRules.js';
import { runValidationRules } from '../../lib/validationEngine.js';
import { toast } from '../../lib/toast.js';
import { useWorkspaceStore } from '../../stores/workspace.js';
import CustomFieldInput from '../customFields/CustomFieldInput.js';
import type { Bucket } from '@mayksel/types';

interface Props {
  bucket?: Bucket | null;
  /** Pre-link to a company when opened from a company detail page */
  defaultCompanyId?: string | undefined;
  defaultCompanyName?: string | undefined;
  onClose: () => void;
}

interface PartnerLink {
  companyId: string;
  companyName: string;
  role: string;
}

interface FormState {
  companyId: string;
  companyName: string;
  name: string;
  status: string;
  value: string;
  probability: string;
  expectedClose: string;
  notes: string;
  partners: PartnerLink[];
}

const STATUS_OPTIONS = ['active', 'on_hold', 'won', 'lost', 'complete'] as const;

export default function BucketModal({ bucket, defaultCompanyId, defaultCompanyName, onClose }: Props) {
  const { t } = useTranslation('buckets');
  const { t: tc } = useTranslation('common');
  const workspace = useWorkspaceStore((s) => s.workspace);
  const overlayRef = useRef<HTMLDivElement>(null);

  const [companySearch, setCompanySearch] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [showPartnerDropdown, setShowPartnerDropdown] = useState(false);

  const createBucket = useCreateBucket();
  const updateBucket = useUpdateBucket(bucket?.id ?? '');
  const { data: customFieldDefs = [] } = useFieldDefinitions('bucket');
  const { data: validationRulesList = [] } = useValidationRules('bucket');
  const activeCustomFields = customFieldDefs.filter((f) => f.isActive);
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, unknown>>(
    bucket?.customFields ?? {},
  );

  const { data: companiesData } = useCompanies({
    ...(companySearch ? { search: companySearch } : {}),
    limit: 20,
  });

  const { data: partnerSearchData } = useCompanies({
    ...(partnerSearch ? { search: partnerSearch } : {}),
    limit: 20,
  });

  const [form, setForm] = useState<FormState>(() => ({
    companyId: bucket?.companyId ?? defaultCompanyId ?? '',
    companyName: bucket?.companyName ?? defaultCompanyName ?? '',
    name: bucket?.name ?? '',
    status: bucket?.status ?? 'active',
    value: bucket?.value ?? '',
    probability: bucket?.probability != null ? String(bucket.probability) : '',
    expectedClose: bucket?.expectedClose ?? '',
    notes: bucket?.notes ?? '',
    partners: bucket?.partners?.map((p) => ({
      companyId: p.companyId,
      companyName: p.companyName,
      role: p.role ?? '',
    })) ?? [],
  }));

  const [errors, setErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  function selectCompany(id: string, name: string) {
    setForm((prev) => ({ ...prev, companyId: id, companyName: name }));
    setCompanySearch('');
    setShowCompanyDropdown(false);
  }

  function addPartner(companyId: string, companyName: string) {
    if (form.partners.some((p) => p.companyId === companyId)) return;
    if (companyId === form.companyId) return; // don't add main company as partner
    setForm((prev) => ({
      ...prev,
      partners: [...prev.partners, { companyId, companyName, role: '' }],
    }));
    setPartnerSearch('');
    setShowPartnerDropdown(false);
  }

  function removePartner(companyId: string) {
    setForm((prev) => ({
      ...prev,
      partners: prev.partners.filter((p) => p.companyId !== companyId),
    }));
  }

  function updatePartnerRole(companyId: string, role: string) {
    setForm((prev) => ({
      ...prev,
      partners: prev.partners.map((p) => p.companyId === companyId ? { ...p, role } : p),
    }));
  }

  function validate(): boolean {
    const newErrors: Record<string, string> = {};
    if (!form.companyId) newErrors.company = 'Company is required';
    if (!form.name.trim()) newErrors.name = 'Name is required';
    if (form.value && !/^\d+(\.\d{1,2})?$/.test(form.value)) newErrors.value = 'Invalid value';
    if (form.probability) {
      const n = parseInt(form.probability, 10);
      if (isNaN(n) || n < 0 || n > 100) newErrors.probability = 'Must be 0-100';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate()) return;

    const trigger = bucket ? 'on_update' : 'on_create';
    const entityData: Record<string, unknown> = {
      name: form.name.trim(),
      status: form.status,
      value: form.value,
      probability: form.probability,
      expectedClose: form.expectedClose,
      notes: form.notes.trim(),
      ...customFieldValues,
    };
    const violations = runValidationRules(validationRulesList, entityData, trigger);
    const blocking = violations.filter((v) => v.actionType !== 'show_warning');
    if (blocking.length > 0) {
      blocking.forEach((v) => toast.error(v.message));
      return;
    }
    violations.filter((v) => v.actionType === 'show_warning').forEach((v) => toast.error(v.message));

    const base = {
      name: form.name.trim(),
      status: form.status as 'active' | 'won' | 'lost' | 'on_hold' | 'complete',
      currency: workspace?.currency ?? 'USD',
      tags: [] as string[],
      customFields: customFieldValues,
      notes: form.notes.trim() || undefined,
      partners: form.partners.map((p) => ({
        companyId: p.companyId,
        role: p.role.trim() || undefined,
      })),
    };

    const withOptionals = {
      ...base,
      ...(form.value ? { value: form.value } : {}),
      ...(form.probability ? { probability: parseInt(form.probability, 10) } : {}),
      ...(form.expectedClose ? { expectedClose: form.expectedClose } : {}),
    };

    try {
      if (bucket) {
        await updateBucket.mutateAsync(withOptionals);
        toast.success(t('toast.updated'));
      } else {
        await createBucket.mutateAsync({ ...withOptionals, companyId: form.companyId });
        toast.success(t('toast.created'));
      }
      onClose();
    } catch (_err) {
      toast.error(bucket ? t('toast.update_error') : t('toast.create_error'));
    }
  }

  const isPending = createBucket.isPending || updateBucket.isPending;

  const linkedPartnerIds = new Set([form.companyId, ...form.partners.map((p) => p.companyId)]);
  const availablePartners = (partnerSearchData?.data ?? []).filter((c) => !linkedPartnerIds.has(c.id));
  const availableCompanies = (companiesData?.data ?? []);

  // The bucket label from workspace settings (e.g. "Project", "Job")
  const bucketLabel = workspace?.bucketLabel ?? t('list.title');

  return (
    <div
      ref={overlayRef}
      role="dialog"
      aria-modal="true"
      aria-label={bucket ? t('form.edit_title') : t('form.create_title')}
      onClick={(e) => { if (e.target === overlayRef.current) onClose(); }}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
        zIndex: 'var(--z-modal)', display: 'flex',
        alignItems: 'center', justifyContent: 'center', padding: 'var(--space-4)',
      }}
    >
      <div style={{
        background: 'var(--color-bg)', borderRadius: 'var(--radius-xl)',
        width: '100%', maxWidth: 560, maxHeight: '90dvh', overflow: 'auto',
        boxShadow: 'var(--shadow-xl)', animation: 'page-enter 200ms var(--ease-out)',
      }}>
        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: 'var(--space-5) var(--space-6)', borderBottom: '1px solid var(--color-border)',
        }}>
          <h2 style={{ fontSize: 'var(--text-lg)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
            {bucket ? t('form.edit_title') : t('form.create_title')}
          </h2>
          <button onClick={onClose} aria-label={tc('actions.close')}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex' }}>
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div style={{ padding: 'var(--space-6)', display: 'flex', flexDirection: 'column', gap: 'var(--space-5)' }}>

            {/* Company (locked if editing or defaultCompanyId provided) */}
            {!bucket && (
              <Field label={t('form.company_label')} error={errors.company} required>
                {form.companyId ? (
                  <div style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: 'var(--space-2) var(--space-3)',
                    border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                    fontSize: 'var(--text-sm)',
                  }}>
                    <span style={{ color: 'var(--color-text)' }}>{form.companyName}</span>
                    {!defaultCompanyId && (
                      <button type="button"
                        onClick={() => setForm((prev) => ({ ...prev, companyId: '', companyName: '' }))}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex' }}>
                        <X size={14} />
                      </button>
                    )}
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text" value={companySearch} autoFocus
                      onChange={(e) => { setCompanySearch(e.target.value); setShowCompanyDropdown(true); }}
                      onFocus={() => setShowCompanyDropdown(true)}
                      onBlur={() => setTimeout(() => setShowCompanyDropdown(false), 150)}
                      placeholder={t('form.company_placeholder')}
                      style={inputStyle(!!errors.company)}
                    />
                    {showCompanyDropdown && availableCompanies.length > 0 && (
                      <div style={dropdownStyle}>
                        {availableCompanies.map((co) => (
                          <button key={co.id} type="button"
                            onMouseDown={() => selectCompany(co.id, co.name)}
                            style={dropdownItemStyle}
                            onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                            onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                            {co.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </Field>
            )}

            {/* Name */}
            <Field label={bucketLabel + ' name'} error={errors.name} required>
              <input type="text" value={form.name} autoFocus={!!bucket || !!defaultCompanyId}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder={t('form.name_placeholder')} style={inputStyle(!!errors.name)} />
            </Field>

            {/* Status */}
            <Field label={t('form.status_label')}>
              <select value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                style={{ ...inputStyle(false), appearance: 'auto' }}>
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>{t(`status.${s}`)}</option>
                ))}
              </select>
            </Field>

            {/* Value + Probability row */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-4)' }}>
              <Field label={t('form.value_label')} error={errors.value}>
                <input type="text" value={form.value}
                  onChange={(e) => setForm({ ...form, value: e.target.value })}
                  placeholder={t('form.value_placeholder')} style={inputStyle(!!errors.value)} />
              </Field>
              <Field label={t('form.probability_label')} error={errors.probability}>
                <input type="number" min="0" max="100" value={form.probability}
                  onChange={(e) => setForm({ ...form, probability: e.target.value })}
                  style={inputStyle(!!errors.probability)} />
              </Field>
            </div>

            {/* Expected close */}
            <Field label={t('form.expected_close_label')}>
              <input type="date" value={form.expectedClose}
                onChange={(e) => setForm({ ...form, expectedClose: e.target.value })}
                style={inputStyle(false)} />
            </Field>

            {/* Partner companies */}
            <div>
              <label style={labelStyle}>{t('form.partners_label')}</label>
              <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-text-muted)', marginTop: 2, marginBottom: 'var(--space-3)' }}>
                {t('form.partners_hint')}
              </p>

              {form.partners.length > 0 && (
                <div style={{
                  border: '1px solid var(--color-border)', borderRadius: 'var(--radius-md)',
                  overflow: 'hidden', marginBottom: 'var(--space-3)',
                }}>
                  {form.partners.map((partner) => (
                    <div key={partner.companyId} style={{
                      display: 'grid', gridTemplateColumns: '1fr auto',
                      alignItems: 'center', gap: 'var(--space-2)',
                      padding: 'var(--space-2) var(--space-3)',
                      borderBottom: '1px solid var(--color-gray-100)',
                    }}>
                      <div>
                        <p style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)', color: 'var(--color-text)' }}>
                          {partner.companyName}
                        </p>
                        <input type="text" value={partner.role}
                          onChange={(e) => updatePartnerRole(partner.companyId, e.target.value)}
                          placeholder={t('form.partner_role_placeholder')}
                          style={{
                            marginTop: 2, width: '100%', border: 'none', background: 'transparent',
                            fontSize: 'var(--text-xs)', color: 'var(--color-text-secondary)',
                            padding: 0, outline: 'none',
                          }}
                        />
                      </div>
                      <button type="button" onClick={() => removePartner(partner.companyId)}
                        aria-label={`Remove ${partner.companyName}`}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-400)', display: 'flex', padding: 4 }}>
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ position: 'relative' }}>
                <div style={{ position: 'relative' }}>
                  <Plus size={14} style={{
                    position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)',
                    color: 'var(--color-text-muted)', pointerEvents: 'none',
                  }} />
                  <input
                    type="text" value={partnerSearch}
                    onChange={(e) => { setPartnerSearch(e.target.value); setShowPartnerDropdown(true); }}
                    onFocus={() => setShowPartnerDropdown(true)}
                    onBlur={() => setTimeout(() => setShowPartnerDropdown(false), 150)}
                    placeholder={t('form.add_partner')}
                    style={{ ...inputStyle(false), paddingLeft: 30, fontSize: 'var(--text-sm)' }}
                  />
                </div>
                {showPartnerDropdown && availablePartners.length > 0 && (
                  <div style={dropdownStyle}>
                    {availablePartners.map((co) => (
                      <button key={co.id} type="button"
                        onMouseDown={() => addPartner(co.id, co.name)}
                        style={dropdownItemStyle}
                        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-bg-subtle)')}
                        onMouseLeave={(e) => (e.currentTarget.style.background = 'none')}>
                        {co.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Notes */}
            <Field label={t('form.notes_label')}>
              <textarea value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={3}
                style={{ ...inputStyle(false), resize: 'vertical', fontFamily: 'var(--font-sans)' }} />
            </Field>
          </div>

          {/* Custom Fields */}
          {activeCustomFields.length > 0 && (
            <div style={{ padding: '0 var(--space-6) var(--space-4)' }}>
              <p className="label" style={{ marginBottom: 'var(--space-3)' }}>
                {t('section_custom_fields', { ns: 'customFields' })}
              </p>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                {activeCustomFields.map((field) => (
                  <Field key={field.id} label={field.label}>
                    <CustomFieldInput
                      field={field}
                      value={customFieldValues[field.key]}
                      onChange={(val) => setCustomFieldValues((prev) => ({ ...prev, [field.key]: val }))}
                    />
                  </Field>
                ))}
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{
            display: 'flex', gap: 'var(--space-3)', justifyContent: 'flex-end',
            padding: 'var(--space-4) var(--space-6)', borderTop: '1px solid var(--color-border)',
          }}>
            <button type="button" onClick={onClose} disabled={isPending} style={cancelBtnStyle}>
              {tc('actions.cancel')}
            </button>
            <button type="submit" disabled={isPending} style={{
              ...saveBtnStyle,
              cursor: isPending ? 'not-allowed' : 'pointer',
              opacity: isPending ? 0.7 : 1,
            }}>
              {isPending ? tc('actions.loading') : tc('actions.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({ label, children, error, required }: {
  label: string; children: React.ReactNode; error?: string | undefined; required?: boolean | undefined;
}) {
  return (
    <div>
      <label style={labelStyle}>
        {label}
        {required && <span style={{ color: 'var(--color-danger)', marginLeft: 2 }}>*</span>}
      </label>
      <div style={{ marginTop: 'var(--space-1)' }}>{children}</div>
      {error && <p style={{ fontSize: 'var(--text-xs)', color: 'var(--color-danger)', marginTop: 4 }}>{error}</p>}
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)',
  color: 'var(--color-text)', display: 'block',
};

function inputStyle(hasError: boolean): React.CSSProperties {
  return {
    width: '100%', padding: 'var(--space-2) var(--space-3)',
    border: hasError ? '1px solid var(--color-danger)' : '1px solid var(--color-border)',
    borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
    color: 'var(--color-text)', background: 'var(--color-bg)', boxSizing: 'border-box',
  };
}

const dropdownStyle: React.CSSProperties = {
  position: 'absolute', top: '100%', left: 0, right: 0,
  background: 'var(--color-bg)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', boxShadow: 'var(--shadow-lg)',
  zIndex: 'var(--z-dropdown)', maxHeight: 200, overflow: 'auto', marginTop: 2,
};

const dropdownItemStyle: React.CSSProperties = {
  display: 'block', width: '100%', textAlign: 'left',
  padding: 'var(--space-2) var(--space-3)',
  fontSize: 'var(--text-sm)', color: 'var(--color-text)',
  background: 'none', border: 'none', cursor: 'pointer',
};

const cancelBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', border: '1px solid var(--color-border)',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  color: 'var(--color-text-secondary)', background: 'var(--color-bg)', cursor: 'pointer',
};

const saveBtnStyle: React.CSSProperties = {
  padding: 'var(--space-2) var(--space-5)', background: 'var(--color-brand)',
  color: 'var(--color-text-inverse)', border: 'none',
  borderRadius: 'var(--radius-md)', fontSize: 'var(--text-sm)',
  fontWeight: 'var(--font-medium)',
};
